/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Cognito wiring. Values are read from Vite env vars at build time. In
 * personal-phase development they live in apps/web/.env.local (gitignored
 * — see infra-stack CfnOutputs for the actual values).
 *
 * Required Vite env vars:
 *   VITE_COGNITO_USER_POOL_ID
 *   VITE_COGNITO_USER_POOL_CLIENT_ID
 *   VITE_API_BASE_URL                (HTTPS endpoint of the API Gateway)
 *
 * The user pool is configured for SRP with TOTP MFA per packages/infra/lib/auth-stack.ts.
 */

const userPoolId = import.meta.env['VITE_COGNITO_USER_POOL_ID'] as string | undefined;
const userPoolClientId = import.meta.env['VITE_COGNITO_USER_POOL_CLIENT_ID'] as string | undefined;

export const apiBaseUrl = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';

if (!userPoolId || !userPoolClientId) {
  // Fail loud in the browser console; the Authenticator renders a blank
  // screen on misconfig otherwise. Personal-phase only — once live, this
  // is a build-time invariant.
  console.error(
    'Missing Cognito env vars. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_USER_POOL_CLIENT_ID in apps/web/.env.local',
  );
}

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: userPoolId ?? '',
      userPoolClientId: userPoolClientId ?? '',
    },
  },
});

/**
 * Pulls the current ID token for authenticated tRPC requests. Returns
 * null when no session — tRPC procedures are authedProcedure so the
 * server rejects unauthenticated calls (defense in depth; the
 * Authenticator already prevents this UI-side).
 */
export async function getIdToken(): Promise<string | null> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ?? null;
}
